import '.a{display:flex;}.ssr-plugin'
import '.b{align-items:center;}.ssr-plugin'
import '.do{gap:2px;}.ssr-plugin'
import '.gl{scrollbar-width:none;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.ey{margin-left:auto;}.ssr-plugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
} from 'lexical'
import { HeadingNode } from '@lexical/rich-text'
import { useLayoutEffect, useState } from 'react'
import {
  $findMatchingParent,
  mergeRegister,
  $getNearestNodeOfType,
} from '@lexical/utils'
import { INSERT_EMBED_COMMAND } from './plugins/EmbedPlugin.js'
import { ListNode } from '@lexical/list'
import { $isLinkNode } from '@lexical/link'
import { colors } from '../../tokens/colors.js'
import { useIsMac } from '../../hooks/useIsMac.js'
import { styled, Style } from 'inlines'
import { Bold } from './Toolbar/Bold.js'
import { Italic } from './Toolbar/Italic.js'
import { Underline } from './Toolbar/Underline.js'
import { StrikeThrough } from './Toolbar/StrikeThrough.js'
import { SelectTextStyle } from './Toolbar/SelectTextStyle.js'
import { Separator } from '../Separator/index.js'
import { SelectTextAlign } from './Toolbar/SelectTextAlign.js'
import { InsertLink } from './Toolbar/InsertLink.js'
import { SelectList } from './Toolbar/SelectList.js'
import { Quote } from './Toolbar/Quote.js'
import { ShowMetrics } from './Toolbar/ShowMetrics.js'
import { Undo } from './Toolbar/Undo.js'
import { Redo } from './Toolbar/Redo.js'
import { SmallMoreOptions } from './Toolbar/SmallMoreOptions.js'
import { AddEmbedModal } from './Toolbar/AddEmbedModal.js'
import { IconButton } from '../IconButton/index.js'
import { SelectImageOptions } from './Toolbar/SelectImageOptions.js'

type ToolbarProps = {
  fullScreen?: boolean
  setFullScreen?: (x: boolean) => void
  isSmall?: boolean
  disabled?: boolean
  style?: Style
}

export function Toolbar({
  fullScreen,
  setFullScreen,
  isSmall,
  disabled,
  style,
}: ToolbarProps) {
  const [editor] = useLexicalComposerContext()
  const [canRedo, setCanRedo] = useState(false)
  const [canUndo, setCanUndo] = useState(false)
  const [nodeType, setNodeType] = useState('')
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [isLink, setIsLink] = useState(false)
  const [align, setAlign] = useState('left')
  const [stats, setStats] = useState({ wordCount: 0, characterCount: 0 })
  const [visibleMetric, setVisibleMetric] = useState('words')
  const isMac = useIsMac()

  useLayoutEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          const selection = $getSelection()
          if (!$isRangeSelection(selection)) return

          const anchorNode = selection.anchor.getNode()
          const parent = anchorNode.getParent()

          setIsBold(selection.hasFormat('bold'))
          setIsItalic(selection.hasFormat('italic'))
          setIsUnderline(selection.hasFormat('underline'))
          setIsStrikethrough(selection.hasFormat('strikethrough'))

          let matchingParent
          if ($isLinkNode(parent)) {
            matchingParent = $findMatchingParent(
              anchorNode,
              (parentNode) =>
                $isElementNode(parentNode) && !parentNode.isInline(),
            )
          }

          setAlign(
            ($isElementNode(matchingParent)
              ? matchingParent.getFormatType()
              : $isElementNode(anchorNode)
                ? anchorNode.getFormatType()
                : anchorNode.getParent()?.getFormatType()) || 'left',
          )

          const parentList = $getNearestNodeOfType<ListNode>(
            anchorNode,
            ListNode,
          )
          if (parentList) {
            setNodeType(parentList.getListType())
            return
          }

          setIsLink($isLinkNode(anchorNode) || (parent && $isLinkNode(parent)))

          const anchorClosestRootNode =
            anchorNode.getKey() === 'root'
              ? anchorNode
              : $findMatchingParent(anchorNode, (e) =>
                  $isRootOrShadowRoot(e.getParent()),
                )
          const nodeType = anchorClosestRootNode?.getType() ?? ''
          if (nodeType === 'heading') {
            setNodeType(
              `heading-${(anchorClosestRootNode as HeadingNode).getTag()}`,
            )
            return
          }
          setNodeType(nodeType)
        })
      }),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload)
          return false
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload)
          return false
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          const textContent = $getRoot().getTextContent()
          const segmenter = new Intl.Segmenter('en', { granularity: 'word' })
          const words = Array.from(segmenter.segment(textContent)).filter(
            (e) => e.isWordLike,
          )

          setStats({
            wordCount: words.length,
            characterCount: words.reduce((acc, curr) => {
              acc += curr.segment.length
              return acc
            }, 0),
          })
        })
      }),
    )
  }, [editor])

  // useLayoutEffect(() => {
  //   return mergeRegister(
  //     editor.registerCommand(
  //       KEY_MODIFIER_COMMAND,
  //       (event) => {
  //         const { code, metaKey, ctrlKey } = event
  //         if (code === 'KeyK' && isMac ? metaKey : ctrlKey) {
  //           editor.dispatchCommand(TOGGLE_LINK_COMMAND, isLink ? null : '')
  //         }
  //         return false
  //       },
  //       COMMAND_PRIORITY_NORMAL,
  //     ),
  //   )
  // }, [editor, isMac, isLink])

  const StyledSeparator = () => (
    <Separator
      orientation="vertical"
      style={{ height: '12px', margin: '0px 6px' }}
    />
  )

  const iconButtonSize = fullScreen ? 'regular' : 'small'

  return (
    <styled.div className="a b do gl h"
      style={{
        padding: fullScreen ? '12px' : 8,
        borderBottom: disabled
          ? `1px solid ${colors.neutral10Adjusted}`
          : `1px solid ${colors.neutral20Adjusted}`,
        pointerEvents: disabled ? 'none' : 'auto',
        height: fullScreen ? 60 : 40,
        ...style,
      }}
    >
      {fullScreen && (
        <>
          <IconButton
            size="regular"
            icon="arrow-left"
            onClick={() => {
              setFullScreen(false)
            }}
          />{' '}
          <StyledSeparator />
        </>
      )}
      <SelectTextStyle
        nodeType={nodeType}
        disabled={disabled}
        size={iconButtonSize}
      />
      <StyledSeparator />
      {!nodeType.startsWith('heading') && (
        <Bold isBold={isBold} disabled={disabled} size={iconButtonSize} />
      )}
      <Italic isItalic={isItalic} disabled={disabled} size={iconButtonSize} />
      <Underline
        isUnderline={isUnderline}
        disabled={disabled}
        size={iconButtonSize}
      />
      {!isSmall && (
        <>
          <StrikeThrough
            isStrikethrough={isStrikethrough}
            disabled={disabled}
            size={iconButtonSize}
          />
          <StyledSeparator />
          <SelectTextAlign
            align={align}
            disabled={disabled}
            size={iconButtonSize}
          />
          <StyledSeparator />
          <InsertLink
            isLink={isLink}
            disabled={disabled}
            size={iconButtonSize}
          />
        </>
      )}
      <SelectImageOptions disabled={disabled} size={iconButtonSize} />
      {!isSmall && (
        <>
          <StyledSeparator />
          <SelectList
            nodeType={nodeType}
            disabled={disabled}
            size={iconButtonSize}
          />
          <StyledSeparator />
          <Quote
            nodeType={nodeType}
            disabled={disabled}
            size={iconButtonSize}
          />
          <AddEmbedModal
            onSave={(html) => {
              editor.update(() => {
                editor.dispatchCommand(INSERT_EMBED_COMMAND, {
                  html: html,
                })
              })
            }}
          >
            <IconButton
              tooltip="Insert embed"
              size={iconButtonSize}
              icon="embed"
              disabled={disabled}
            />
          </AddEmbedModal>

          <StyledSeparator />
          <Undo disabled={!canUndo || disabled} size={iconButtonSize} />
          <Redo disabled={!canRedo || disabled} size={iconButtonSize} />

          <div className="a b ey do"
          >
            {fullScreen && (
              <ShowMetrics
                stats={stats}
                visibleMetric={visibleMetric}
                setVisibleMetric={setVisibleMetric}
                disabled={disabled}
                size={iconButtonSize}
              />
            )}
            <IconButton
              icon={fullScreen ? 'full-screen-off' : 'full-screen-on'}
              tooltip={fullScreen ? 'exit Full screen' : 'Full screen'}
              disabled={disabled}
              size={iconButtonSize}
              onClick={() => setFullScreen(!fullScreen)}
            />
          </div>
        </>
      )}

      {isSmall && (
        <SmallMoreOptions
          nodeType={nodeType}
          isStrikethrough={isStrikethrough}
          isLink={isLink}
          align={align}
          disabled={disabled}
        />
      )}
    </styled.div>
  )
}
